import { ColumnData } from 'ui/tableHeader/constants';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { isString } from 'utils/helpers';

export const sortColumns = (columnOrder: string[], columns: ColumnData[]) => {
  const sortedCols: ColumnData[] = [];
  const extraCols: ColumnData[] = [];
  columns.forEach((col) => {
    const idx = columnOrder.indexOf(col.value);
    if (idx >= 0) {
      sortedCols[idx] = col;
    } else {
      extraCols.push(col);
    }
  });
  return [...sortedCols, ...extraCols];
};

/**
 * paginateResults
 * Utility function that can be used to paginate results based on page and pageSize.
 * Useful for client-side pagination. There also are a few custom hooks that makes use of this function,
 * depending on the specific intended use case. Take a look at:
 * /src/ui/table/hooks/useClientPaginatedRows.tsx
 * /src/ui/table/hooks/useClientPaginatedState.tsx
 */

export const paginateResults = <DataType>(
  data: DataType[],
  paginationDetails: { page: number; pageSize: number },
) =>
  (data || []).slice(
    (paginationDetails.page - 1) * paginationDetails.pageSize,
    paginationDetails.page * paginationDetails.pageSize,
  );

/**
 * sortRows
 * Utility function that can be used to sort rows based on a key and direction.
 * Useful for client-side sorting. There also are a few custom hooks that makes use of this function,
 * depending on the specific intended use case. Take a look at:
 * /src/ui/table/hooks/useSortRows.tsx
 * /src/ui/table/hooks/useClientPaginatedState.tsx
 */
export const sortRows = <DataType>(
  data: DataType[] = [],
  sortKey: keyof DataType,
  sortDirection: SORT_DIRECTIONS | string = SORT_DIRECTIONS.DESCENDING,
) => {
  const result = data.sort((val1, val2) => {
    const formattedValue1 = isString(val1[sortKey]) ? val1[sortKey].toLowerCase() : val1[sortKey];
    const formattedValue2 = isString(val2[sortKey]) ? val2[sortKey].toLowerCase() : val2[sortKey];

    if (sortDirection === SORT_DIRECTIONS.DESCENDING || sortDirection === 'desc') {
      return formattedValue1 > formattedValue2 ? -1 : 1;
    }
    return formattedValue2 > formattedValue1 ? -1 : 1;
  });
  return result;
};
